/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { TeamSide, YardConverter } from "@prestosports/prestostats-core/Common";
import classnames from "classnames";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { CSSTransition } from "react-transition-group";
import { useEventContext } from "../../../state/events/context";
import { useGameContextContext } from "../../../state/game-context/context";
import useFieldConstants from "./utils/FieldConfiguration";
import ballPositionStyles from "../styles/BallPosition.styles";
import { useTeamsContext } from "../../../state/teams/context";
import { useFootballContext } from "../../../state/football-play/context";

const PositionIndicator = ({
	fieldLengthWithoutEndZones,
	endZonePercentage,
	swapTeams
}) => {
	const { state: footballState } = useFootballContext();
	const isPatAttempt = footballState.isPatAttempt();

	const { state } = useGameContextContext();
	const fieldPosition = state.fieldPosition();
	const triggerPAT = state.triggerPAT();
	const triggerEndTime = state.triggerEndTime();

	const { state: teamState } = useTeamsContext();
	const awayTeam = teamState.awayTeam();
	const homeTeam = teamState.homeTeam();

	const yardConverter = new YardConverter(fieldLengthWithoutEndZones);
	const { teamSide, yard } =
		yardConverter.convertFromFieldPosition(fieldPosition);

	let displayName = teamSide;
	if (teamSide === TeamSide.Home && homeTeam?.abbrName)
		displayName = homeTeam?.abbrName.substring(0, 3);
	if (teamSide === TeamSide.Visitor && awayTeam?.abbrName)
		displayName = awayTeam?.abbrName.substring(0, 3);

	let relativeYard = swapTeams ? fieldLengthWithoutEndZones - yard : yard;
	if (teamSide === TeamSide.Home) {
		relativeYard = fieldLengthWithoutEndZones - relativeYard;
	}
	const fieldPositionPercentage =
		(relativeYard * (100 - endZonePercentage * 2)) / fieldLengthWithoutEndZones;

	const currentOffset = `calc(${endZonePercentage}% + ${fieldPositionPercentage}%)`;

	const [offset, setOffset] = useState({
		current: null,
		previous: null
	});

	const [animate, setAnimate] = useState(false);

	const classes = ballPositionStyles({ offset });

	useEffect(() => {
		setOffset({
			previous: offset.current,
			current: currentOffset
		});

		setAnimate(offset && offset.current !== offset.previous);
	}, [currentOffset]);

	return (
		<CSSTransition
			in={animate}
			timeout={1000}
			classNames={{
				enter: classes.slideEnter,
				enterActive: classes.slideEnterActive
			}}
			onEntered={() => {
				setAnimate(false);
			}}
		>
			<div className={classes.ballPosition}>
				<BallPositionTooltip
					title={
						triggerEndTime ? "END" : isPatAttempt || triggerPAT ? "PAT" : displayName
					}
					subtitle={yard || null}
				/>
			</div>
		</CSSTransition>
	);
};

export const BallPositionTooltip = ({ title, subtitle }) => {
	const classes = ballPositionStyles({ subtitle });

	return (
		<span id="positionIndicator" className={classes.positionText}>
			<Grid
				direction="column"
				alignItems="center"
				justifyContent="center"
				container
				className={classes.positionGrid}
			>
				<Grid item>{title?.toUpperCase()}</Grid>
				<Grid item>{subtitle}</Grid>
			</Grid>
		</span>
	);
};

const BallPosition = () => {
	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();
	const classes = ballPositionStyles({ homeTeam, awayTeam });

	const { state } = useEventContext();
	const currentEventSettings = state.currentEventSettings();

	const { state: gameContextState } = useGameContextContext();
	const swapTeams = gameContextState.swapTeams();

	const {
		fieldLength,
		zoneLength,
		zonesPerTeamQty,
		zoneLengthPct,
		endZoneLengthPct,
		firstDownYardSet,
		fieldAreaSet
	} = useFieldConstants(currentEventSettings);

	const AREA_COLORS = {
		redZone: "rgba(211, 47, 47, 0.22)",
		noRunZone: "rgba(255, 167, 38, 0.30)",
		endZone: "rgba(120, 120, 120, 0.22)",
		custom: "rgba(33, 150, 243, 0.22)"
	};

	const yardToLeftPct = (yard) => {
		const base =
			endZoneLengthPct + (yard / fieldLength) * (100 - endZoneLengthPct * 2);
		return swapTeams ? 100 - base : base;
	};

	const createAreaOverlay = () => {
		return (fieldAreaSet || []).map((area, index) => {
			const a = yardToLeftPct(area.start);
			const b = yardToLeftPct(area.end);
			const left = Math.min(a, b);
			const width = Math.abs(b - a);
			return (
				<Box
					// eslint-disable-next-line react/no-array-index-key
					key={`area-${index}`}
					className={classes.areaBand}
					style={{
						left: `${left}%`,
						width: `${width}%`,
						backgroundColor: AREA_COLORS[area.type] || AREA_COLORS.custom
					}}
				/>
			);
		});
	};

	const createFirstDownLines = () => {
		return (firstDownYardSet || []).map((yard) => (
			<Box
				key={`fd-${yard}`}
				className={classes.firstDownLine}
				style={{ left: `${yardToLeftPct(yard)}%` }}
			/>
		));
	};

	const createZones = (isVisitor) => {
		const zones = [];
		for (let i = 0; i < zonesPerTeamQty; i += 1) {
			zones.push(
				<Grid
					item
					style={{ width: `${zoneLengthPct}%` }}
					key={isVisitor ? `vz${i}` : `hz${i}`}
				>
					<div className={classes.fieldBar} />
					<div className={classnames(classes.zoneBar, { visitor: isVisitor })} />
				</Grid>
			);
		}

		return zones;
	};

	const createEndZone = (isVisitor) => (
		<Grid
			item
			style={{ width: `${endZoneLengthPct}%` }}
			key={isVisitor ? "vez" : "hez"}
		>
			<div className={classnames(classes.endZoneBar, { visitor: isVisitor })} />
		</Grid>
	);

	const createLabels = () => {
		const labels = [];
		const markers = zonesPerTeamQty * 2 + 1;
		const half = markers / 2;

		for (let i = 0; i < markers; i += 1) {
			const fieldLabel = i <= half ? zoneLength * i : fieldLength - zoneLength * i;

			if (fieldLabel) {
				labels.push({ fieldLabel, index: i });
			}
		}

		return (
			<Grid container align="center" justifyContent="center">
				<Grid
					item
					style={{
						width: `${endZoneLengthPct}%`,
						flexGrow: 1,
						textAlign: "left",
						paddingLeft: 5
					}}
					className={classnames(classes.endZoneText, { visitor: true })}
				>
					End Zone
				</Grid>
				{labels.map(({ fieldLabel, index }) => {
					return (
						<Grid
							item
							key={index}
							style={{
								width: `${zoneLengthPct}%`
							}}
							className={classnames({
								[classes.zoneBarText]: fieldLabel !== fieldLength / 2,
								[classes.zoneBarMiddleText]: fieldLabel === fieldLength / 2
							})}
						>
							{fieldLabel}
						</Grid>
					);
				})}
				<Grid
					item
					style={{
						width: `${endZoneLengthPct}%`,
						flexGrow: 1,
						textAlign: "right",
						paddingRight: 5
					}}
					className={classes.endZoneText}
				>
					End Zone
				</Grid>
			</Grid>
		);
	};

	return fieldLength && endZoneLengthPct ? (
		<Box mb={2}>
			<PositionIndicator
				id="positionIndicator"
				fieldLengthWithoutEndZones={fieldLength}
				endZonePercentage={endZoneLengthPct}
				swapTeams={swapTeams}
			/>
			<Box className={classes.fieldOverlayWrapper}>
				<Grid
					container
					align="center"
					direction={swapTeams ? "row-reverse" : "row"}
				>
					{createEndZone(true)}
					{createZones(true)}
					{createZones(false)}
					{createEndZone(false)}
				</Grid>
				<Box className={classes.markerOverlay}>
					{createAreaOverlay()}
					{createFirstDownLines()}
				</Box>
			</Box>
			{createLabels()}
		</Box>
	) : null;
};

export default BallPosition;
